<!--
  Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
  This information is confidential and proprietary to GM Global Technology
  Operations LLC and may not be used, modified, copied or distributed.
  -->

<br/>
<h1 class="page-header">IO Matrix ICDs</h1>

@if (permission()) {
  <div class="icd-workflow">
    @if (saving || exporting || resetting) {
      <p-progressBar mode="indeterminate" [style]="{ height: '6px' }"></p-progressBar>
    }
    <div class="context-row">
      <label>Saved IO Matrix</label>
      <p-autoComplete
        [(ngModel)]="selectedMatrixLabel"
        [suggestions]="filteredSavedMatrixOptions"
        [forceSelection]="true"
        [dropdown]="true"
        dropdownMode="blank"
        [minQueryLength]="1"
        [showClear]="true"
        [disabled]="saving || exporting || resetting"
        placeholder="Type to search Object Code | Object Code Inst | Architecture | Cadence"
        (completeMethod)="filterSavedMatrixSuggestions($event)"
        (onSelect)="onSavedMatrixSelected()"
        (onClear)="onSavedMatrixCleared()"></p-autoComplete>
        @if (showVariantDropdown()) {
            <label>Variant</label>
            <p-select [options]="matrixVariantOptions()"
                      optionLabel="label"
                      optionValue="value"
                      [(ngModel)]="selectedVariant"
                      [disabled]="!selectedMatrixKey || saving || exporting || resetting"
                      (onChange)="onVariantSelectionChanged()"></p-select>
        }
    </div>


    <div class="button-row">
      <p-button label="Edit ICD Template" [disabled]="!selectedMatrixKey || saving || exporting || resetting"
                (click)="chooseTemplateMode('edit')" class="mr-2"></p-button>
      <p-button label="Create New ICD Template" [disabled]="!selectedMatrixKey || saving || exporting || resetting"
                (click)="chooseTemplateMode('create')" class="mr-2"></p-button>
    </div>

    @if (refMapWarning) {
      <p-message severity="warn"
                 data-testid="ref-map-warning"
                 [closable]="false"
                 [text]="refMapWarningText"></p-message>
    }

    @if (templateCadenceUpgradeAvailable) {
      <p-message severity="warn"
                 text="A newer saved IO Matrix cadence is available. Merge to create template copy for the newer cadence."></p-message>
    }
    @if (templateLatestCadenceAlreadyExistsWarning) {
      <p-message severity="warn"
                 text="You are working on an older cadence template, and an equivalent ICD template already exists at the latest cadence."></p-message>
    }
    @if (templateDeviceIoUpgradeWarning) {
      <p-message severity="warn"
                 text="Device IO has a newer cadence than IO Matrix. Upgrade IO Matrix first to enable template cadence merge."></p-message>
    }

    @if (templateMode === 'edit') {
      <div class="context-row">
        <label>Existing ICD Template</label>
        <p-autoComplete
          [(ngModel)]="selectedExistingTemplateLabel"
          [suggestions]="filteredExistingTemplateOptions"
          [forceSelection]="true"
          [dropdown]="true"
          dropdownMode="blank"
          [minQueryLength]="1"
          [showClear]="true"
          placeholder="Type to search existing templates"
          [disabled]="saving || exporting || resetting"
          (completeMethod)="filterExistingTemplateSuggestions($event)"
          (onSelect)="onExistingTemplateSelected()"
          (onClear)="onExistingTemplateCleared()"></p-autoComplete>
        <p-button label="Add Template Column" [disabled]="!templateMode || !selectedMatrixKey || saving || exporting || resetting"
                  (click)="addTemplateColumnFromSelection()" class="mr-2"></p-button>
      </div>
    }

    @if (templateMode === 'create') {
      <div class="context-row">
        <label>Program</label>
          <p-autoComplete
              [(ngModel)]="selectedProgramForNewTemplate"
              [suggestions]="filteredProgramOptions"
              [forceSelection]="true"
              [minQueryLength]="1"
              placeholder="Type to search programs"
              [showClear]="true"
              (completeMethod)="filterProgramSuggestions($event)"
              (onSelect)="onProgramSelected()"
              (onClear)="onProgramCleared()"></p-autoComplete>
        <label>Model Year</label>
        <p-select [options]="modelYearOptions" [disabled]="saving || exporting || resetting"
                  [(ngModel)]="selectedModelYearForNewTemplate"></p-select>
        <label>RPO</label>
        <input pInputText [(ngModel)]="selectedRpoForNewTemplate" [disabled]="saving || exporting || resetting"/>
        <label>Additional RPOs</label>
        <input pInputText [(ngModel)]="selectedAdditionalRposForNewTemplate" [disabled]="saving || exporting || resetting"/>
        <label>Build Phase</label>
        <input pInputText [(ngModel)]="selectedBuildPhaseForNewTemplate" [disabled]="saving || exporting || resetting"/>
        <p-button label="Add Template Column" [disabled]="!templateMode || !selectedMatrixKey || saving || exporting || resetting"
                  (click)="addTemplateColumnFromSelection()" class="mr-2"></p-button>
      </div>
    }

      @if(templateColumns.length > 0) {
        <div class="button-row">
          @if (showTemplateCadenceMergeAction) {
            <p-button label="Merge New Cadence"
                      [disabled]="!selectedMatrixKey || saving || exporting || resetting"
                      (click)="runTemplateCadenceUpgrade()" class="mr-2"></p-button>
          }
          <p-button label="Copy Template" icon="pi pi-copy"
                    [disabled]="templateColumns.length === 0 || saving || exporting || resetting"
                    (click)="openCopyTemplateDialog()" class="mr-2"></p-button>
          <p-button label="Clear Template Columns" severity="warn" icon="pi pi-trash"
                    [disabled]="templateColumns.length === 0 || saving || exporting || resetting"
                    (click)="clearTemplateColumns()" class="mr-2"></p-button>
          <p-button label="Reset Changes" severity="warn" icon="pi pi-replay"
                    [disabled]="!dataChanged || saving || exporting || resetting"
                    [loading]="!!(saving || exporting || resetting)"
                    (click)="resetTemplateChanges()" class="mr-2"></p-button>
          <p-button label="Save Templates" icon="pi pi-save" [disabled]="templateColumns.length === 0 || !dataChanged || saving || exporting || resetting"
                    [loading]="!!(saving || exporting || resetting)" (click)="saveTemplateValues()"></p-button>
        </div>
      }
    <div class="context-row">
      <label>View</label>
      <p-select [options]="ioMatrixViewOptions"
                optionLabel="label"
                optionValue="value"
                [(ngModel)]="selectedView"
                [disabled]="saving || exporting || resetting"></p-select>
    </div>
    <ag-grid-angular style="width: 100%; height: 55vh;" class="ag-theme-balham"
                     [columnDefs]="templateCols"
                     [rowData]="templateRows"
                     [defaultColDef]="defaultColDef"
                     [autoSizeStrategy]="autoSizeStrategy"
                     [rowClassRules]="rowClassRules"
                     [pagination]="true"
                     [paginationPageSize]="paginationPageSize"
                     [paginationPageSizeSelector]="paginationPageSizeSelector"
                     (cellEditingStopped)="cellEditingStopped($event, templateRows)"
                     (gridReady)="onGridReady($event)">
    </ag-grid-angular>

    <p-dialog header="Copy ICD Template Column"
              [(visible)]="copyDialogVisible"
              [modal]="true"
              [closable]="true"
              [draggable]="false"
              [resizable]="false"
              [style]="{width: '46rem'}"
              [breakpoints]="{'960px': '90vw', '640px': '96vw'}"
              (onHide)="closeCopyTemplateDialog()">
      <div class="copy-template-dialog">
        <div class="copy-template-row">
          <label class="copy-template-label">Source Template</label>
          @if (templateColumns.length === 1) {
            <span class="copy-template-source-readonly">{{ copyTemplateSourceLabel }}</span>
          } @else {
            <p-select class="copy-template-input"
                      [options]="copyTemplateSourceOptions"
                      optionLabel="label"
                      optionValue="value"
                      [(ngModel)]="copySourceColumnField"
                      placeholder="Select a source template"
                      appendTo="body"></p-select>
          }
        </div>
        <div class="copy-template-row">
          <label class="copy-template-label">Program</label>
          <p-autoComplete class="copy-template-input"
              [(ngModel)]="copyTargetProgram"
              [suggestions]="filteredCopyTargetProgramOptions"
              [forceSelection]="true"
              [minQueryLength]="1"
              placeholder="Type to search programs"
              [showClear]="true"
              appendTo="body"
              (completeMethod)="filterCopyTargetProgramSuggestions($event)"></p-autoComplete>
        </div>
        <div class="copy-template-row">
          <label class="copy-template-label">Model Year</label>
          <p-select class="copy-template-input"
                    [options]="modelYearOptions"
                    [(ngModel)]="copyTargetModelYear"
                    placeholder="Select a model year"
                    appendTo="body"></p-select>
        </div>
        <div class="copy-template-row">
          <label class="copy-template-label">RPO</label>
          <input class="copy-template-input" pInputText [(ngModel)]="copyTargetRpo"/>
        </div>
        <div class="copy-template-row">
          <label class="copy-template-label">Additional RPOs</label>
          <input class="copy-template-input" pInputText [(ngModel)]="copyTargetAdditionalRpos"/>
        </div>
        <div class="copy-template-row">
          <label class="copy-template-label">Build Phase</label>
          <input class="copy-template-input" pInputText [(ngModel)]="copyTargetBuildPhase"/>
        </div>
      </div>
      <ng-template pTemplate="footer">
        <p-button label="Cancel" severity="secondary" (click)="closeCopyTemplateDialog()" class="mr-2"></p-button>
        <p-button label="Copy" icon="pi pi-copy"
                  [disabled]="!copyDialogValid"
                  (click)="executeCopyTemplate()"></p-button>
      </ng-template>
    </p-dialog>
  </div>
}
